import { Tween } from "@westpierstudio/luma/createjs";
import { EventEmitter } from "@westpierstudio/luma/events";
import { AssetSpec, SpriteAssetSpec, TextAssetSpec } from "@westpierstudio/luma/factory/PixiFactory";
import { Orientation } from "@westpierstudio/luma/layout";
import { View } from "@westpierstudio/luma/mvc";
import { Flow } from "@westpierstudio/luma/utils/flow";
import { Resettable } from "@westpierstudio/luma/utils/reset";
import { GameServices } from "game/GameServices";
import { GameState } from "game/PrinceOfThievesGameModel";
import { Container } from "pixi.js";
import { GameEvent } from "servertypes/ServerEvents";
import { Bounds, implementBoundedContainer } from "utils/BoundedContainer";
import { TextFactory, TextFactorySpec, TextFactoryTokens, TextOptions, TextStyleSpec } from "utils/TextFactory";
import { Reels } from "./reels/Reels";
import { OrientationSpec } from "servertypes/SharedTypes";


interface GameMessageSettings {
    splashScreenMessageMS: number
    idleMessageMS: number
    activeMessageMS: number
}

interface GameLogoSpec {
    position: OrientationSpec<AssetSpec>,
    gameLogo: SpriteAssetSpec,
    freeGameLogo: SpriteAssetSpec,
    gameMessages: AssetSpec,
    leftMessage: SpriteAssetSpec,
}

interface GameLogoMessageSpec {
    bonusMessage: TextFactorySpec
    baseMessage1: TextFactorySpec
    baseMessage2: TextFactorySpec
    freeMessage1: TextFactorySpec
    freeMessage2: TextFactorySpec
    freeMessage3: TextFactorySpec
    tokens: TextFactoryTokens
    bowText: SpriteAssetSpec
    bonusText: SpriteAssetSpec
    baseLogo: SpriteAssetSpec
    freeLogo: SpriteAssetSpec
    leftTextNum: TextAssetSpec,
    leftTextWays: TextAssetSpec,
    leftTextContainer: AssetSpec,
    leftTextBounds: Bounds
}

export class GameLogo extends View implements Resettable {
    private readonly IDLE_MS;
    private readonly ACTIVE_TIMER_MS;

    private readonly spec: GameLogoSpec;
    // private readonly gameLogoSprite: Sprite;
    // private readonly gameLogoTex: Texture;
    // private readonly freeGameLogoTex: Texture;
    private readonly gameMessageContainer: Container;

    private readonly leftMessage: Container;
    private readonly bonusInitiatedContainer: Container;
    private readonly baseGameMessageContainers: Container[] = [];
    private readonly freeGameMessageContainers: Container[] = [];

    private readonly flow;
    private readonly reels: Reels;
    private messageIndex: number = 0;
    private currentSpinType: GameEvent = GameEvent.INITIAL_SPIN;
    private idleGameState: boolean = true;
    private idleMessageLoop: boolean = true;

    textFactory: TextFactory;

    constructor(protected override readonly services: GameServices, reels: Reels) {
        super(services);
        const { factory, resources } = this.services;

        this.spec = resources.getSpec(`gamelogo`);
        const msgspec: GameLogoMessageSpec = resources.getSpec(`gamemessages`);
        this.gameMessageContainer = factory.createContainer(this.spec.gameMessages, this);
        // this.gameLogoSprite = factory.createSprite(this.spec.gameLogo, this);
        // this.gameLogoTex = this.gameLogoSprite.texture;
        // this.freeGameLogoTex = resources.getTexture(this.spec.freeGameLogo._key);
        this.reels = reels;

        this.textFactory = new TextFactory(this.services, `yellow`, msgspec.tokens);
        this.baseGameMessageContainers.push(factory.createSprite(msgspec.baseLogo, this.gameMessageContainer));
        this.baseGameMessageContainers.push(this.textFactory.createTextFromSpec(msgspec.baseMessage1, this.gameMessageContainer));
        this.baseGameMessageContainers.push(this.textFactory.createTextFromSpec(msgspec.baseMessage2, this.gameMessageContainer));
        const leftMessageNumText = [...resources.getMessage(`messageLeftNum`)].join(`\n`);
        const leftMessageWaysText = [...resources.getMessage(`messageLeftWays`)].join(`\n`);
        this.leftMessage = factory.createContainer(msgspec.leftTextContainer, this);
        const leftNum = factory.createText(leftMessageNumText, { ...msgspec.leftTextNum }, this.leftMessage);
        const leftWays = factory.createText(leftMessageWaysText, { ...msgspec.leftTextWays }, this.leftMessage);
        const yoffset = leftNum.height - (leftNum.height + leftWays.height) / 2;
        leftNum.y += yoffset;
        leftWays.y += yoffset;

        implementBoundedContainer(this.leftMessage, msgspec.leftTextBounds);

        this.bonusInitiatedContainer = this.textFactory.createTextFromSpec(msgspec.bonusMessage, this.gameMessageContainer);

        this.baseGameMessageContainers[0].visible = true;

        this.freeGameMessageContainers.push(factory.createSprite(msgspec.freeLogo, this.gameMessageContainer));
        this.freeGameMessageContainers.push(this.textFactory.createTextFromSpec(msgspec.freeMessage1, this.gameMessageContainer));
        this.freeGameMessageContainers.push(this.textFactory.createTextFromSpec(msgspec.freeMessage2, this.gameMessageContainer));
        this.freeGameMessageContainers.push(this.textFactory.createTextFromSpec(msgspec.freeMessage3, this.gameMessageContainer));

        const settings: GameMessageSettings = resources.getSetting(`gameMessageDuration`);
        this.IDLE_MS = settings.idleMessageMS;
        this.ACTIVE_TIMER_MS = settings.activeMessageMS;
        this.flow = new Flow(new EventEmitter());

        this.reorient(this.services.layout.orientation);
        this.services.layout.onReorient.add(() => this.reorient(this.services.layout.orientation));
    }

    showMessageBase(index: number) {
        this.hideMessages();
        this.baseGameMessageContainers[index].visible = true;
    }

    showMessageFree(index: number) {
        this.hideMessages();
        this.freeGameMessageContainers[index].visible = true;
    }

    bonusInitiatedMessage() {
        this.hideMessages();
        this.bonusInitiatedContainer.visible = true;
    }

    private hideMessages() {
        this.bonusInitiatedContainer.visible = false;
        for (const container of this.baseGameMessageContainers) {
            container.visible = false;
        }
        for (const container of this.freeGameMessageContainers) {
            container.visible = false;
        }
    }

    setSpinType(spin: GameEvent) {
        Tween.removeTweens(this);
        const portrait = this.services.layout.orientation === `portrait`;
        switch (spin) {
            case GameEvent.INITIAL_SPIN:
                this.currentSpinType = spin;
                // this.gameLogoSprite.visible = portrait;
                // this.gameLogoSprite.texture = this.gameLogoTex;
                // this.services.factory.applySpec(this.gameLogoSprite, this.spec.gameLogo);
                this.showMessageBase(0);
                this.leftMessage.visible = true;
                this.messageIndex = 0;
                this.idleGameState ? this.baseGameIdleLoop() : this.baseGameActiveLoop();
                break;
            case GameEvent.FREE_SPIN:
                this.currentSpinType = spin;
                // this.gameLogoSprite.visible = portrait;
                // this.gameLogoSprite.texture = this.freeGameLogoTex;
                // this.services.factory.applySpec(this.gameLogoSprite, this.spec.freeGameLogo);
                this.showMessageFree(0);
                this.leftMessage.visible = false;
                this.messageIndex = 0;
                this.freeGameLoop();
                break;
            default:
                break;
        }
    }

    eventChange(ev: GameState) {
        const idle = ev === GameState.IDLE;
        if (this.idleGameState != idle) {
            this.checkIdleState(idle);
            this.idleGameState = idle;
        }
    }

    private checkIdleState(idle: boolean) {
        Tween.removeTweens(this);
        if (idle) { //new idle state
            Tween.get(this)
                .wait(this.IDLE_MS).call(() => {
                    if (this.idleGameState && !this.idleMessageLoop) {
                        this.idleMessageLoop = true;
                        this.baseGameIdleLoop(true);
                    }
                });
        }
        else { //no longer idle
            if (this.idleMessageLoop) {
                this.idleMessageLoop = false;
                this.baseGameActiveLoop();
            }
        }
    }

    resumeFreeGames() {
        this.eventChange(GameState.SPIN);
        this.setSpinType(GameEvent.FREE_SPIN);
    }

    override reset(): void {
        this.eventChange(this.idleGameState ? GameState.IDLE : GameState.SPIN);
        this.setSpinType(this.currentSpinType);
    }

    baseGameIdleLoop(instant: boolean = false) {
        this.flow.reset()
            .addDelay(instant ? 0 : this.IDLE_MS)
            .addCallback(() => {
                this.messageIndex = instant ? 0 : (this.messageIndex + 1) % this.baseGameMessageContainers.length;
                this.showMessageBase(this.messageIndex);
                this.baseGameIdleLoop();
            });
        this.flow.run();
    }

    baseGameActiveLoop() {
        this.flow.reset()
            .addDelay(this.ACTIVE_TIMER_MS)
            .addSignalBlock(this.reels.signals.spinComplete)
            .addCallback(() => {
                this.messageIndex = (this.messageIndex + 1) % this.baseGameMessageContainers.length;
                this.showMessageBase(this.messageIndex);
                this.baseGameActiveLoop();
            });
        this.flow.run();
    }

    freeGameLoop() {
        Tween.removeTweens(this);
        this.flow.reset()
            .addDelay(this.ACTIVE_TIMER_MS)
            .addSignalBlock(this.reels.signals.spinComplete)
            .addCallback(() => {
                this.messageIndex = (this.messageIndex + 1) % this.freeGameMessageContainers.length;
                this.showMessageFree(this.messageIndex);
                this.freeGameLoop();
            });
        this.flow.run();
    }

    reorient(orientation: Orientation): void {
        const { factory, layout } = this.services;
        factory.applySpec(this, this.spec.position[layout.orientation]);
        if (orientation === `landscape`) {
            this.landscape();
        } else {
            this.portrait();
        }
    }

    private portrait() {
        // this.gameLogoSprite.visible = true;
        this.gameMessageContainer.visible = true;
        this.leftMessage.visible = this.currentSpinType == GameEvent.INITIAL_SPIN;
    }

    private landscape() {
        // this.gameLogoSprite.visible = false;
        this.gameMessageContainer.visible = true;
        this.leftMessage.visible = this.currentSpinType == GameEvent.INITIAL_SPIN;
    }
}
